<div class="step-wrapper">
  <span
    i18n="
      XMSG|Step Setup into
      text@@organizations.unified-step-setup.stepSetupIntroText"
    >Use this quick-start wizard to set up your app in the unified services
    framework and to create the minimum supplementary resources required for it.
    <app-help-popover
      i18n-title="
        XTIT|Step Setup read more
        title@@organizations.unified-step-setup.stepSetupReadMoreTitle"
      headerTitle="About this Quick Start Wizard"
      i18n-linkText="
        XLNK|Step Setup read more
        link@@organizations.unified-step-setup.stepSetupReadMoreLink"
      linkText="Read more"
      width="34rem"
      [body]="wizardPopover"
    ></app-help-popover>

    <ng-template #wizardPopover>
      <div>
        Use this quick-start wizard as a starting point for setting up your app
        in the unified services framework. <br /><br />
        The wizard uses the information that you provide to create the minimum
        required resources to support your app, including
        ResourceTypeDefinition, ManagedService, and ServiceMetadata resources.
        Additional resources will also be generated based on the capabilities
        you choose for your app. Resources are created in the folder that you're
        currently in, and in a dedicated resource group also created by the
        wizard. <br /><br />

        If you're setting up an SAP BTP app that is SAP-managed, the wizard will
        also generate YAML files that you can use immediately to execute a first
        test (level-0) of the provisioning flow. <br /><br />
        TIPS:
        <ul>
          <li class="margin-bottom">
            Check out the help and documentation links provided throughout the
            wizard to guide you.
          </li>
          <li>
            At the end of the wizard, you'll find links to documentation to help
            you continue your onboarding journey.
          </li>
        </ul>
      </div>
    </ng-template>
  </span>

  <form [formGroup]="form" autocomplete="off" class="form-scroll">
    <div [formGroupName]="STEP_SETUP_FORM_GROUPS.STEP_SETUP">
      <h3
        class="section-title"
        i18n="
          XGRP|Step Setup Basic Info
          title@@organizations.unified-step-setup.stepSetupBasicInfoTitle"
      >
        Basic Info
      </h3>
      <app-container
        [body]="basicInfoContainer"
        class="margin-bottom"
      ></app-container>
      <ng-template #basicInfoContainer>
        <app-reactive-input
          id="app-display-name-field"
          name="appDisplayName"
          i18-label="XFLD|Step Setup App display name label@@organizations.unified-step-setup.stepSetupAppDisplayNameLabel"
          label="App Display Name"
          [formControlName]="STEP_SETUP_FORM_CONTROLS.APP_DISPLAY_NAME"
          popoverTitle="App Display Name"
          [popoverDescription]="appDisplayNameInfoHelp"
          placeholder="Enter the display name of your app"
        >
        </app-reactive-input>
        <p
          class="field-hint-text"
          i18="XMSG|Step Setup App display name hint@@organizations.unified-step-setup.stepSetupAppDisplayNameHint"
        >Avoid using temporary or test names in a production scenario.</p>
        <ng-template
          #appDisplayNameInfoHelp
          i18="XMSG|Step Setup App display name popover body@@organizations.unified-step-setup.stepSetupAppDisplayNamePopoverBody"
        >
            The display name of your app. Example: Procurement Dashboard <br/><br/>
            The display name can contain only lowercase and uppercase alphabetical
            letters (a-z, A-Z), numbers (0-9), and spaces. The maximum length is
            {{ getMaxDisplayNameLength() }} characters. Use a descriptive name with spaces between words and title case.<br/><br/>
            <b>Note:</b> This wizard uses your app's display name to automatically generate technical names for several
            components, such as the technical app ID,
            service names, and destination names. While your display name can be changed later, these technical names
            are immutable and cannot be changed
            after the app is created. <br/><br/>
            <b>Important:</b> While most technical names are not customer-facing, if you select the Integration
            capability, a technical identifier visible to your
            customers is also generated from your app name in the FormationType resource and cannot be changed later.
            <br/><br/>
            <b>Recommendation</b>: Choose a name that accurately reflects your solution, such as an official brand
            name. Do not use temporary, test, or placeholder names in production scenarios.
        </ng-template>

        <div [formGroupName]="STEP_SETUP_FORM_GROUPS.OPERATIONAL_DATA">
          <div class="margin-top">
            <fd-form-input-message-group
              [triggers]="['mouseenter', 'mouseleave']"
            >
              <app-helpful-label
                [required]="true"
                for="operationalMode"
                i18-label="XFLD|Step Setup App Operational Mode label@@organizations.unified-step-setup.stepSetupOperationalModeLabel"
                label="SAP App Operational Mode"
                [width]="'27rem'"
                [popoverBody]="operationalModePopoverBody"
                i18-popoverTitle="XTIT|Step Setup App Operational Mode popover title@@organizations.unified-step-setup.stepSetupOperationalPopoverTitle"
                popoverTitle="SAP App Operational Mode"
              >
              </app-helpful-label>
              <fd-select
                fdCompact
                [required]="true"
                id="operationalMode"
                [formControlName]="STEP_SETUP_FORM_CONTROLS.OPERATIONAL_MODE"
                fillControlMode="fit-content"
                [inline]="false"
              >
                <fd-option [value]="OperationalMode.SAP"
                  >SAP-Managed (for SaaS App)</fd-option
                >
                <fd-option [value]="OperationalMode.CUSTOMER"
                  >Customer-Managed (for SAP BTP PaaS App)
                </fd-option>
              </fd-select>

              <ng-template
                #operationalModePopoverBody
                i18="XMSG|Step Setup App Operational Mode popover body@@organizations.unified-step-setup.stepSetupOperationalModePopoverBody"
              >
                When offering an SAP app via the unified services framework, you
                need to decide which operational mode best fits your app. The
                two main operational modes to consider are
                <strong>SAP-managed for SaaS apps</strong> and
                <strong>customer-managed for SAP BTP PaaS apps</strong>.
                <br /><br />
                Note: With the introduction of the
                <a
                  fd-link
                  href="https://github.tools.sap/CPA/product-standards/blob/main/requirements/Integration/INTG-04R1/INTG-04R1.md"
                  target="_blank"
                  >INTG-04R1 Product Standard</a
                >, all SAP SaaS applications must be SAP-managed and provide a
                setup that is fully automated, eliminating the need for any
                manual intervention or setup steps by customers.
              </ng-template>
            </fd-form-input-message-group>
          </div>
        </div>
        @if (this.showStagingParameters) {
          <div id="stagingParameter" class="margin-top">
            <fd-form-input-message-group
              [triggers]="['mouseenter', 'mouseleave']"
            >
              <app-helpful-label
                [required]="true"
                i18-label="XFLD|Staged Parameters Setup label@@organizations.unified-step-setup.stepSetupBtpAppLabel"
                label="Staged Landscape Setup"
                [popoverBody]="stagedParametersPopoverBody"
                i18-label="XTIT|Step Setup BTP App Popover Title@@organizations.unified-step-setup.stepSetupBtpAppPopoverTitle"
                popoverTitle="Staged Landscape Setup"
              >
              </app-helpful-label>
              <div class="radio-container">
                @for (param of stagingParameters; track param) {
                  <fd-radio-button
                    [value]="param.infix"
                    [formControlName]="
                      STEP_SETUP_FORM_CONTROLS.STAGING_PARAMETER
                    "
                    [name]="'stagingParameter'"
                  >
                    {{ param.wizardName }}
                  </fd-radio-button>
                }
              </div>
              <ng-template
                #stagedParametersPopoverBody
                i18="XMSG|Staged Parameters Steup popover body@@organizations.unified-step-setup.stepSetupStagedParametersPopoverBody"
              >
                Select the stage at which your app will be set up.<br /><br />
                Applications in the Canary landscape can be set up in the
                <strong>Dev</strong> or <strong>Test</strong> stage, while
                applications in the <strong>Live</strong> landscape can be set
                up in the <strong>Pre-Production</strong> or
                <strong>Production</strong> stage.<br /><br />
                Note: For the <strong>Dev</strong> and
                <strong>Pre-Production</strong> stages, the stage name is
                automatically appended to both your app’s display name and
                technical name to distinguish them from higher-stage
                environments.<br /><br />
                More information:
                <a
                  fd-link
                  href="https://pages.github.tools.sap/atom-cfs/atom-docs/docs/getting-started/staged-development/"
                  [emphasized]="true"
                  target="_blank"
                  >Application Staged Development</a
                >
              </ng-template>
            </fd-form-input-message-group>
          </div>
        }
      </ng-template>
      <h3
        class="section-title"
        i18n="
          XGRP|Title of capabilities
          section@@organizations.unified-step-setup.stepSetupCapabilitiesTitle"
      >
        Capabilities
      </h3>
      <app-container
        [body]="capabilitiesContainer"
        class="margin-bottom"
      ></app-container>
      <ng-template #capabilitiesContainer>
        <div [formGroupName]="STEP_SETUP_FORM_GROUPS.OPERATIONAL_DATA">
          <div [formGroupName]="STEP_SETUP_FORM_GROUPS.CAPABILITIES">
            <div class="capability-container">
              <fd-checkbox
                [formControlName]="CapabilityType.Commercialization"
                label="Product Metadata"
                [fdCompact]="true"
                [disabled]="true"
              ></fd-checkbox>
              <app-help-popover
                placement="bottom"
                [body]="productMetadatBody"
                width="34rem"
                headerTitle="Product Metadata"
              ></app-help-popover>
            </div>
            <div class="capability-container">
              <fd-checkbox
                [formControlName]="CapabilityType.Provisioning"
                label="Provisioning"
                [fdCompact]="true"
              ></fd-checkbox>
              <app-help-popover
                placement="bottom"
                [body]="provisioningBody"
                headerTitle="Provisioning"
              ></app-help-popover>
            </div>
            <div class="capability-container">
              <fd-checkbox
                [formControlName]="CapabilityType.Integration"
                label="Integration"
                [fdCompact]="true"
              ></fd-checkbox>
              <app-help-popover
                placement="bottom"
                [body]="integrationBody"
                headerTitle="Integration"
              ></app-help-popover>
            </div>
            @if (!hasStagingInfix()) {
              <div class="capability-container">
                <fd-checkbox
                  [formControlName]="CapabilityType.Metering"
                  label="Metering"
                  [fdCompact]="true"
                ></fd-checkbox>
                <app-help-popover
                  placement="bottom"
                  [body]="meteringBody"
                  headerTitle="Metering"
                ></app-help-popover>
              </div>
            }
          </div>
          <ng-template
            #productMetadatBody
            i18n="
              XMSG|Step Setup Capability Product Metadata popover
              body@@organizations.unified-step-setup.stepSetupCapabilityProductMetadataPopoverBody"
          >
            Defining your app's product metadata is a mandatory step for
            onboarding your app to Unified Services. <br /><br />
            For this capability, the wizard creates a ServiceMetadata resource.
            This resource contains the basic metadata of your app, including its
            technical name, display name, and description.
            <br /><br />
            Later, during the commercialization process, you may need to enhance
            this resource with an additional configuration that is relevant to
            the registration of your app in the Service Lifecycle Management
            (SLM) tool. <br /><br />
            In addition, the ServiceMetadata resource is used for performing a
            commercial mapping; if this is required, you must enhance it with
            the relevant fields. See
            <a
              fd-link
              href="https://pages.github.tools.sap/atom-cfs/atom-docs/docs/how-to-guides/configure-commercial-mapping/"
              [emphasized]="true"
              target="_blank"
              >Configure Commercial Mapping</a
            >.
          </ng-template>
          <ng-template
            #commercializationBody
            i18n="
              XMSG|Step Setup Capability Commercialization popover
              body@@organizations.unified-step-setup.stepSetupCapabilityCommercializationPopoverBody"
          >
            Commercializing your app is a mandatory step to having it available
            globally; however, most of the steps become relevant only at a later
            phase of your onboarding. <br /><br />
            The wizard creates two resources that are relevant for the
            commercialization process:
            <ul>
              <li class="margin-bottom">
                <strong>ServicePublishConfiguration:</strong>
                Publishes your resource type (the one defined in your
                ResourceTypeDefinition) in the 'test' folder, which is also
                created by the wizard.<br /><br />
                Later, during the commercialization process, you may need to
                enhance this resource with an additional configuration that is
                relevant to making it available for customers.
              </li>
              <li>
                <strong>ServiceMetadata:</strong>
                This resource is created by the wizard, and contains the basic
                metadata of your app, including its technical name, display
                name, and description. <br /><br />
                Later, during the commercialization process, you may need to
                enhance this resource with an additional configuration that is
                relevant to the registration of your app in the Service
                Lifecycle Management (SLM) tool, which is responsible for
                listing your app in SAP for Me. In addition, the ServiceMetadata
                resource is used for performing a commercial mapping; if this is
                required, you must enhance it with the relevant fields (see
                <a
                  fd-link
                  href="https://pages.github.tools.sap/atom-cfs/atom-docs/docs/business-use-cases/billing-and-reporting/#commercial-mapping"
                  [emphasized]="true"
                  target="_blank"
                  >Commercial Mapping</a
                >).
              </li>
            </ul>
          </ng-template>

          <ng-template
            #provisioningBody
            i18n="
              XMSG|Step Setup Capability Provisioning popover
              body@@organizations.unified-step-setup.stepSetupCapabilityProvisioningPopoverBody"
          >
            The unified services framework automatically provisions your app in
            accordance with the 'Automated Provisioning and Integration Setup'
            product standard (INTG-TG04).<br /><br />
            More information:
            <ul>
              <li>
                <a
                  fd-link
                  href="https://pages.github.tools.sap/product-standards/portal/docs/requirements/Integration/groups/INTG-TG04/"
                  [emphasized]="true"
                  target="_blank"
                  >INTG-TG04 Product Standard</a
                >
              </li>
              <li>
                <a
                  fd-link
                  href="https://pages.github.tools.sap/atom-cfs/atom-docs/docs/how-to-guides/provision-your-app/provision-your-saas-app-urm/"
                  [emphasized]="true"
                  target="_blank"
                  >Provision Your SAP-Managed SaaS App</a
                >
              </li>
            </ul>
          </ng-template>
          <ng-template
            #integrationBody
            i18n="
              XMSG|Step Setup Capability Integration popover
              body@@organizations.unified-step-setup.stepSetupCapabilityIntegrationPopoverBody"
          >
            The unified services framework enables the automatic integration of
            your app with line of business (LOB) systems, such as SAP S/4HANA
            Cloud and SAP Ariba Sourcing.
            <div>
              <br />More information:
              <ul>
                <li>
                  <a
                    fd-link
                    href="https://pages.github.tools.sap/atom-cfs/atom-docs/docs/how-to-guides/integrate-your-app/"
                    [emphasized]="true"
                    target="_blank"
                    >Integrate Your App</a
                  >
                </li>
              </ul>
            </div>
          </ng-template>
          <ng-template
            #meteringBody
            i18n="
              XMSG|Step Setup Capability Metering popover
              body@@organizations.unified-step-setup.stepSetupCapabilityMeteringPopoverBody"
          >
            The unified services framework enables the metering of your app's
            usage information for billing and reporting purposes. <br /><br />
            The wizard creates a Metering Account resource that registers your
            app in Unified Metering.

            <div>
              <br />
              More information:
              <ul>
                <li>
                  <a
                    fd-link
                    href="https://pages.github.tools.sap/atom-cfs/atom-docs/docs/how-to-guides/meter-your-app/"
                    [emphasized]="true"
                    target="_blank"
                    >Meter Your App</a
                  >
                </li>
              </ul>
            </div>
          </ng-template>
        </div>
      </ng-template>

      <ng-container>
        <div [formGroupName]="STEP_SETUP_FORM_GROUPS.PRODUCT_METADATA">
          <h3
            class="section-title"
            i18n="
              XGRP|Step Setup Commercialization
              title@@organizations.unified-step-setup.stepSetupCommercializationTitle"
          >
            Product Metadata
          </h3>
          <app-container
            [body]="productMetadata"
            class="margin-bottom"
          ></app-container>
          <ng-template #productMetadata>
            <app-reactive-input
              i18-label="XFLD|Step Setup CLD System Role label@@organizations.unified-step-setup.stepSetupCldSystemRoleLabel"
              label="CLD System Role"
              [formControlName]="STEP_SETUP_FORM_CONTROLS.CLD_SYSTEM_ROLE"
              popoverTitle="CLD System Role"
              [required]="false"
              [popoverDescription]="cldSystemRoleInfoHelp"
              placeholder="Enter the CLD system role"
            >
            </app-reactive-input>
            <ng-template
              #cldSystemRoleInfoHelp
              i18="XMSG|Step Setup CLD System Role popover body@@organizations.unified-step-setup.stepSetupCldSystemRolePopoverBody"
            >
              Enter the CLD system role that represents the app in the Cloud
              Landscape Directory (CLD) within the Service Provider Cockpit
              (SPC).
            </ng-template>

            @if (isServiceVisible) {
              <div class="margin-top">
                <fd-form-input-message-group
                  [triggers]="['mouseenter', 'mouseleave']"
                >
                  <app-helpful-label
                    for="serviceIdCombo"
                    [popoverBody]="serviceIdPopoverBody"
                    i18-popoverTitle="XTIT|Step Select Service Popover Title@@organizations.unified-step-setup.stepSelectServiceIdPopoverTitle"
                    popoverTitle="Service"
                    width="34rem"
                    i18-label="XFLD|Step Select Service label@@organizations.unified-step-setup.stepSelectServiceIdLabel"
                    label="Service"
                  ></app-helpful-label>
                  <ng-template
                    #serviceIdPopoverBody
                    i18="XMSG|Step Select Service ID popover body@@organizations.unified-step-setup.stepSelectServiceIdPopoverBody"
                  >
                    Choose the service that you're developing for SAP BTP.
                    <br /><br />
                    All listed services are retrieved from the
                    <a
                      fd-link
                      class="baseline-align"
                      href="https://inventory.services.tools.sap/cp.portal/site#Shell-home"
                      [emphasized]="true"
                      target="_blank"
                      >Service Inventory.</a
                    >
                    <br /><br />
                    If you can't find your service in the list:
                    <ul>
                      <li>
                        Go to the
                        <a
                          fd-link
                          class="baseline-align"
                          href="https://wiki.wdf.sap.corp/wiki/x/lnA9bw"
                          [emphasized]="true"
                          target="_blank"
                          >Service Inventory wiki</a
                        >
                        for information about creating and updating service
                        entries in the Service Inventory. <br /><br />
                      </li>
                      <li>
                        If your service is defined in the Service Inventory and
                        it's still not showing up in this wizard, you can
                        manually add the service's details in the next step
                        (Review) under the
                        <strong>ServiceMetadata</strong> section. Alternatively,
                        you can add the service information in the YAML of the
                        generated ServiceMetadata resource after the wizard has
                        completed.
                      </li>
                    </ul>
                  </ng-template>
                  <fd-busy-indicator
                    [block]="true"
                    size="m"
                    [loading]="isServicesBusy"
                    ariaLabel="loading"
                  >
                    <fd-combobox
                      fdCompact
                      id="servicesCombo"
                      placeholder="Select a service"
                      [displayFn]="serviceDisplayFunc"
                      [itemTemplate]="serviceColumnTemplate"
                      [communicateByObject]="true"
                      [dropdownValues]="services() || []"
                      [filterFn]="servicesFilterFunc"
                      [formControlName]="STEP_SETUP_FORM_CONTROLS.SERVICE"
                    >
                    </fd-combobox>
                  </fd-busy-indicator>
                  <ng-template #serviceColumnTemplate let-service>
                    <span fd-list-title>{{ service.name }}</span>
                    <span fd-list-secondary>{{ service.key }}</span>
                  </ng-template>
                </fd-form-input-message-group>
              </div>
            }

            @if (
              form
                .get(STEP_SETUP_FORM_GROUPS.STEP_SETUP)
                ?.get(STEP_SETUP_FORM_GROUPS.OPERATIONAL_DATA)
                ?.get(STEP_SETUP_FORM_GROUPS.CAPABILITIES)
                ?.get(CapabilityType.Integration)?.value === true &&
              form.get(PAYLOAD_FORM_GROUPS.PAYLOAD)?.get(PAYLOAD_FORM_GROUPS.MANAGED_APPLICATION_PROVIDER)
            ) {
                <div
                        class="margin-top"
                [formGroup]="
                  $any(
                    form
                      .get(PAYLOAD_FORM_GROUPS.PAYLOAD)
                      ?.get(PAYLOAD_FORM_GROUPS.MANAGED_APPLICATION_PROVIDER)
                  )
                "
              >
                <app-reactive-input
                  i18-label="XFLD|Step Setup Support Component label@@organizations.unified-step-setup.stepSetupSupportComponentLabel"
                  label="Support Component"
                  [formControlName]="PAYLOAD_FORM_CONTROLS.MANAGED_APPLICATION_PROVIDER.SUPPORT_COMPONENT"
                  popoverTitle="Support Component"
                  [required]="false"
                  [popoverDescription]="supportComponentInfoHelp"
                  placeholder="Enter the support component"
                >
                </app-reactive-input>
                <ng-template
                  #supportComponentInfoHelp
                  i18="XMSG|Step Setup Support Component popover body@@organizations.unified-step-setup.stepSetupSupportComponentPopoverBody"
                >
                    Enter the support component for your app so your team can be contacted in case there is an issue with the onboarding of your application.
                    <br/><br/>
                    This information will be added to the generated ManagedApplicationProvider resource.
                    <br/><br/>
                    Note: If you do not enter a support component, the default "BC-CP-MP-SYS" component will be assigned. If needed, update this information when you set the scope of your ManagedApplicationProvider resource to "published".
                </ng-template>
                    <div>Note: If you do not enter a support component, a default value will be assigned.</div>
              </div>
            }
          </ng-template>
        </div>
      </ng-container>
      @if (
        !!operationalMode &&
        form
          .get(STEP_SETUP_FORM_GROUPS.STEP_SETUP)
          ?.get(STEP_SETUP_FORM_GROUPS.OPERATIONAL_DATA)
          ?.get(STEP_SETUP_FORM_GROUPS.CAPABILITIES)
          ?.get(CapabilityType.Provisioning)?.value === true
      ) {
        <h3
          class="section-title"
          i18="XGRP|Step Setup BTP Details section title@@organizations.unified-step-setup.stepSetupBtpDetailsSectionTitle"
        >
          {{ "Provisioning" }}
        </h3>
        @if (oidc === "custom.idp" || oidc === "sap.opscurity") {
          <app-info-strip [infoText]="oidcMessage()"> </app-info-strip>
        }
        <app-container [body]="btpDetails"></app-container>
        <ng-template #btpDetails>
          <div [formGroupName]="STEP_SETUP_FORM_GROUPS.PROVISIONING">
            @if (operationalMode === OperationalMode.SAP) {
              <div id="offeredInSapBtp" class="margin-top">
                <fd-form-input-message-group
                  [triggers]="['mouseenter', 'mouseleave']"
                >
                  <app-helpful-label
                    [required]="true"
                    i18-label="XFLD|Step Setup BTP App label@@organizations.unified-step-setup.stepSetupBtpAppLabel"
                    label="App is Offered in SAP BTP?"
                    [popoverBody]="isBTPPopoverBody"
                    i18-label="XTIT|Step Setup BTP App Popover Title@@organizations.unified-step-setup.stepSetupBtpAppPopoverTitle"
                    popoverTitle="App is Offered in SAP BTP?"
                  >
                  </app-helpful-label>
                  <div class="radio-container">
                    <fd-radio-button
                      [formControlName]="
                        STEP_SETUP_FORM_CONTROLS.IS_BTP_APPLICATION
                      "
                      [value]="IsBTPApplication.YES"
                      [fdCompact]="true"
                      name="btp"
                    >
                      Yes
                    </fd-radio-button>
                    <fd-radio-button
                      [formControlName]="
                        STEP_SETUP_FORM_CONTROLS.IS_BTP_APPLICATION
                      "
                      [value]="IsBTPApplication.NO"
                      [fdCompact]="true"
                      name="btp"
                    >
                      No
                    </fd-radio-button>
                  </div>
                  <ng-template
                    #isBTPPopoverBody
                    i18="XMSG|Step Setup App BTP App popover body@@organizations.unified-step-setup.stepSetupbtpAppPopoverBody"
                  >
                    Specify whether your SAP-managed SaaS app is offered in SAP
                    BTP or not. In other words, whether you’ll be creating an
                    SAP BTP subaccount per customer in your provider's SAP BTP
                    global account.
                  </ng-template>
                </fd-form-input-message-group>
              </div>
            }
            @if (
              operationalMode === OperationalMode.SAP && !isOfferedInSapBtp
            ) {
              <div id="reusingSpcProcedure" class="margin-top">
                <fd-form-input-message-group
                  [triggers]="['mouseenter', 'mouseleave']"
                >
                  <app-helpful-label
                    [required]="true"
                    i18-label="XFLD|Step Reusing SPC label@@organizations.unified-step-setup.reusingSPC"
                    [popoverBody]="isReusingSPC"
                    label="Reusing an SPC Procedure?"
                    popoverTitle="Reusing an SPC Procedure?"
                  >
                  </app-helpful-label>
                  <div class="radio-container">
                    <fd-radio-button
                      [formControlName]="
                        STEP_SETUP_FORM_CONTROLS.IS_REUSING_SPC
                      "
                      [value]="true"
                      [fdCompact]="true"
                      name="reusingSpc"
                    >
                      Yes
                    </fd-radio-button>
                    <fd-radio-button
                      [formControlName]="
                        STEP_SETUP_FORM_CONTROLS.IS_REUSING_SPC
                      "
                      [value]="false"
                      [fdCompact]="true"
                      name="reusingSpc"
                    >
                      No
                    </fd-radio-button>
                  </div>
                  <ng-template
                    #isReusingSPC
                    i18="XMSG|Step Setup App Reusing SPC popover body@@organizations.unified-step-setup.stepSetupReusingSPCPopoverBody"
                  >
                    Choose <strong>Yes</strong> if your app is currently
                    provisioned using an SPC procedure. In this case, you’ll be
                    reusing it to leverage SPC's SPFI implementation.
                  </ng-template>
                </fd-form-input-message-group>
              </div>
            }
            @if (landscapeValues.length > 1) {
              <div class="margin-top">
                <app-helpful-label
                  i18n-label="
                    XFLD|Landscape field
                    input@@organizations.unified-step-setup.landscapeLabel"
                  i18n-popoverTitle="
                    XTIT|Title of the Landscape
                    field@@organizations.unified-step-setup.landscapePopoverTitle"
                  label="Landscape"
                  popoverTitle="Landscape"
                  [popoverBody]="landscapeInfoHelp"
                >
                </app-helpful-label>
                <ng-template
                  #landscapeInfoHelp
                  i18n="
                    XMSG|Step Setup Landscape popover
                    body@@organizations.unified-step-setup.stepSetupLandscapePopoverBody"
                >
                  Choose the SAP BTP landscape where your app is deployed.
                  <br /><br />
                  Our recommendation is to start with Canary, but if your app is
                  available only on Live, then select Live.
                </ng-template>
                @for (value of landscapeValues; track value) {
                  <div fd-form-item>
                    <fd-radio-button
                      [fdCompact]="true"
                      [selectedValue]="
                        form.controls[STEP_SETUP_FORM_GROUPS.STEP_SETUP].value
                          .landscape
                      "
                      [value]="value.key"
                      [disabled]="isGlobalAccountsBusy"
                      name="landscape"
                      [formControlName]="STEP_SETUP_FORM_CONTROLS.LANDSCAPE"
                    >
                      {{ value.title }}
                    </fd-radio-button>
                  </div>
                }
              </div>
            }
            <div id="scenarioField">
              <fd-form-input-message-group
                [triggers]="['mouseenter', 'mouseleave']"
              >
                <app-helpful-label
                  [required]="true"
                  for="scenario"
                  [popoverBody]="scenarioPopoverBody"
                  width="38rem"
                  i18-popoverTitle="XTIT|Step Setup Automation Popover Title@@organizations.unified-step-setup.stepSetupAutomationPopoverTitle"
                  popoverTitle="Automation Provisioner"
                  i18-label="XFLD|Step Setup Automation label@@organizations.unified-step-setup.stepSetupAutomationLabel"
                  label="Automation Provisioner"
                ></app-helpful-label>
                <ng-template
                  #scenarioPopoverBody
                  i18="XMSG|Step Setup Automation popover body@@organizations.unified-step-setup.stepSetupAutomationPopoverBody"
                >
                  Select the automated provisioner for your app. Note that the
                  provisioners available to you depend on your setup, and in
                  some cases, you may only have a single option available.
                  <ul>
                    <li>
                      <strong>Unified Cloud Automation:</strong>
                      Unified Cloud Automation handles all the SAP BTP
                      automation and provisioning orchestration for your app.<br /><br />
                      This is the recommended option for all new applications
                      and provides a simplified, operator-based provisioning
                      model.<br /><br />
                    </li>
                    <li>
                      <strong>Unified Cloud Automation (SPFI-based):</strong>
                      Unified Cloud Automation handles all the SAP BTP
                      automation and provisioning orchestration for your app
                      while leveraging the SPFI protocol.<br /><br />
                      <strong>Note: This option is deprecated.</strong> It
                      remains available only for applications already onboarded
                      using SPFI that require additional dev or test landscapes.
                      <br /><br />
                    </li>
                    <li>
                      <strong>SPC SPFI:</strong> Use your existing SPC procedure
                      using SPC's SPFI implementation. <br /><br />
                    </li>
                    <li>
                      <strong>Own SPFI Implementation:</strong>
                      You manage the entire SPFI implementation and SAP BTP
                      automation by yourself using your own provisioning logic.
                      <br /><br />
                    </li>
                    <li>
                      <strong>Custom Operator:</strong> You implement your own
                      Unified Resource Manager operator to support custom
                      provisioning flows.
                    </li>
                  </ul>
                </ng-template>
                <fd-select
                  fdCompact
                  [required]="true"
                  id="scenario"
                  [formControlName]="STEP_SETUP_FORM_CONTROLS.SCENARIO"
                  fillControlMode="fit-content"
                  [inline]="false"
                >
                  @if (operationalMode == OperationalMode.CUSTOMER) {
                    <fd-option [value]="OperationalScenario.UCA_LEASED"
                      >Unified Cloud Automation</fd-option
                    >
                  }
                  @if (
                    operationalMode == OperationalMode.SAP && isOfferedInSapBtp
                  ) {
                    <fd-option [value]="OperationalScenario.UCA_LEASED"
                      >Unified Cloud Automation (New)</fd-option
                    >
                  }
                  @if (
                    operationalMode == OperationalMode.SAP && isOfferedInSapBtp
                  ) {
                    <fd-option [value]="OperationalScenario.UCA_SPFI"
                      >Unified Cloud Automation (SPFI-based)</fd-option
                    >
                  }
                  @if (
                    Const.FEATURE_UNIFIED_WIZARD_EUPORIE | isFeatureEnabled
                  ) {
                    <fd-option [value]="OperationalScenario.EUPORIE"
                      >Using Euporie and Unified Cloud Automation
                    </fd-option>
                  }
                  @if (
                    operationalMode == OperationalMode.SAP &&
                    !isOfferedInSapBtp &&
                    isReusingSPC
                  ) {
                    <fd-option [value]="OperationalScenario.SPC_SPFI"
                      >SPC SPFI</fd-option
                    >
                  }
                  @if (
                    operationalMode == OperationalMode.SAP &&
                    !isOfferedInSapBtp &&
                    !isReusingSPC
                  ) {
                    <fd-option [value]="OperationalScenario.UCA_LEASED"
                      >Unified Cloud Automation</fd-option
                    >
                  }
                  @if (
                    operationalMode == OperationalMode.SAP &&
                    !isOfferedInSapBtp &&
                    !isReusingSPC
                  ) {
                    <fd-option [value]="OperationalScenario.OWN"
                      >Own SPFI Implementation</fd-option
                    >
                  }
                  @if (
                    operationalMode == OperationalMode.SAP &&
                    !isOfferedInSapBtp &&
                    !isReusingSPC
                  ) {
                    <fd-option [value]="OperationalScenario.CUSTOM"
                      >Custom Operator</fd-option
                    >
                  }
                </fd-select>
              </fd-form-input-message-group>
            </div>
            @if (
              operationalMode === OperationalMode.CUSTOMER ||
              isOfferedInSapBtp ||
              operationalScenario === OperationalScenario.UCA_LEASED
            ) {
              <div id="btp-fields">
                <div id="btp-title">
                  <h3 class="section-title">SAP BTP Details</h3>
                </div>
                <div id="globalAccountField">
                  <fd-form-input-message-group
                    [triggers]="['mouseenter', 'mouseleave']"
                  >
                    <app-helpful-label
                      [required]="true"
                      for="btpGlobalAccount"
                      i18-label="XFLD|Step Setup Provider Global Account label@@organizations.unified-step-setup.stepSetupProviderLabel"
                      label="Provider Global Account"
                      [popoverBody]="btpGlobalAccount"
                      i18-popoverTitle="XTIT|Step Setup Provider Global Account popover title@@organizations.unified-step-setup.stepSetupProviderPopoverTitle"
                      popoverTitle="Provider Global Account"
                    >
                    </app-helpful-label>
                    <fd-busy-indicator
                      [block]="true"
                      size="m"
                      [loading]="isGlobalAccountsBusy"
                      ariaLabel="loading"
                    >
                      <fd-combobox
                        fdCompact
                        [required]="true"
                        id="btpGlobalAccount"
                        [communicateByObject]="true"
                        placeholder="Select a global account"
                        [formControlName]="
                          STEP_SETUP_FORM_CONTROLS.GLOBAL_ACCOUNT
                        "
                        [displayFn]="globalAccountDisplayFn"
                        [dropdownValues]="
                          globalAccounts || [] | sortBy: true : 'displayName'
                        "
                      >
                      </fd-combobox>
                    </fd-busy-indicator>
                    <ng-template
                      #btpGlobalAccount
                      i18="XMSG|Step Setup Provider Global Account popover body@@organizations.unified-step-setup.stepSetupProviderGlobalAccountPopoverBody"
                    >
                      Choose the SAP BTP global account used by the provider of
                      the SAP-managed app.<br /><br />
                      The global accounts listed are the ones that you have
                      access to on the current landscape.
                    </ng-template>
                  </fd-form-input-message-group>
                </div>
                <div id="subaccountField" class="margin-top">
                  <fd-form-input-message-group
                    [triggers]="['mouseenter', 'mouseleave']"
                  >
                    <app-helpful-label
                      [required]="true"
                      for="btpSubaccount"
                      i18-label="XFLD|Step Setup Provider Subaccount label@@organizations.unified-step-setup.stepSetupProviderSubaccountLabel"
                      label="Provider Subaccount"
                      [popoverBody]="btpSubaccount"
                      i18-popoverTitle="XFLD|Step Setup Provider Subaccount Popover Title@@organizations.unified-step-setup.stepSetupProviderSubaccountPopoverTitle"
                      popoverTitle="Provider Subaccount"
                    >
                    </app-helpful-label>
                    <fd-busy-indicator
                      [block]="true"
                      size="m"
                      [loading]="isSubaccountsBusy"
                      ariaLabel="loading"
                    >
                      <fd-combobox
                        fdCompact
                        [required]="true"
                        id="btpSubaccount"
                        [communicateByObject]="true"
                        [disabled]="
                          !form.controls[STEP_SETUP_FORM_GROUPS.STEP_SETUP]
                            .value[STEP_SETUP_FORM_CONTROLS.GLOBAL_ACCOUNT]
                        "
                        [state]="
                          !subaccounts
                            ? undefined
                            : subaccounts.length === 0
                              ? 'error'
                              : undefined
                        "
                        placeholder="Select a subaccount"
                        [formControlName]="STEP_SETUP_FORM_CONTROLS.SUBACCOUNT"
                        [displayFn]="subaccountDisplayFn"
                        [dropdownValues]="
                          subaccounts || [] | sortBy: true : 'displayName'
                        "
                      >
                      </fd-combobox>
                    </fd-busy-indicator>
                    @if (subaccounts?.length === 0) {
                      <fd-form-message type="error"
                        >No available subaccounts
                      </fd-form-message>
                    }
                  </fd-form-input-message-group>
                  <ng-template
                    #btpSubaccount
                    i18="XMSG|Step Setup Provider Subaccount popover body@@organizations.unified-step-setup.stepSetupProviderSubaccountPopoverBody"
                  >
                    Choose an SAP BTP subaccount of the app provider.
                    <br /><br />
                    The subaccounts listed are the ones available in the
                    selected global account. <br /><br />
                    In this subaccount, the wizard will create an instance and
                    binding of the SAP Cloud Management (cis) service's
                    'cloud-automation' plan.
                  </ng-template>
                </div>
                @if (
                  operationalMode === OperationalMode.CUSTOMER ||
                  isOfferedInSapBtp
                ) {
                  <div id="consumerSubaccount" class="margin-top">
                    <fd-form-input-message-group
                      [triggers]="['mouseenter', 'mouseleave']"
                    >
                      <app-helpful-label
                        [required]="false"
                        for="btpTemplateSubaccount"
                        i18-label="XFLD|Step Setup Consumer Subaccount label@@organizations.unified-step-setup.stepSetupProviderConsumerLabel"
                        label="Consumer Test Subaccount (Template)"
                        [popoverBody]="btpTestSubaccount"
                        i18-popoverTitle="XFLD|Step Setup Consumer Subaccount Popover Title@@organizations.unified-step-setup.stepSetupConsumerSubaccountPopoverTitle"
                        popoverTitle="Consumer Test Subaccount (Template)"
                      >
                      </app-helpful-label>
                      <fd-busy-indicator
                        [block]="true"
                        size="m"
                        [loading]="isSubaccountsBusy"
                        ariaLabel="loading"
                      >
                        <fd-combobox
                          fdCompact
                          [required]="false"
                          id="btpTemplateSubaccount"
                          [communicateByObject]="true"
                          [disabled]="
                            !form.controls[STEP_SETUP_FORM_GROUPS.STEP_SETUP]
                              .value[STEP_SETUP_FORM_CONTROLS.GLOBAL_ACCOUNT]
                          "
                          [state]="
                            !subaccounts
                              ? undefined
                              : subaccounts.length === 0
                                ? 'error'
                                : undefined
                          "
                          placeholder="Select a subaccount"
                          [formControlName]="
                            STEP_SETUP_FORM_CONTROLS.TEMPLATE_SUBACCOUNT
                          "
                          [displayFn]="subaccountDisplayFn"
                          [dropdownValues]="
                            subaccounts || [] | sortBy: true : 'displayName'
                          "
                        >
                        </fd-combobox>
                      </fd-busy-indicator>
                      @if (subaccounts?.length === 0) {
                        <fd-form-message type="error"
                          >No available subaccounts
                        </fd-form-message>
                      }
                    </fd-form-input-message-group>
                    <ng-template #btpTestSubaccount>
                      Select a subaccount that contains all the relevant
                      resources that will be needed by the consumer of the app.
                      <br /><br />
                      The wizard will provide an automation script in the
                      AutomationSolution, which you can use to provision your
                      app. Note that the script will not be fully complete.
                      You'll need to fine tune it using the Cloud Automation
                      Designer tool; for example, to define dependencies between
                      modules.
                      <br /><br />
                      If you do not choose a consumer subaccount, then the
                      AutomationSolution will not contain an automation script
                      for the provisioning of your app. You'll need to add this
                      yourself.
                      <br /><br />
                      The subaccounts listed are the ones available in the
                      selected global account.
                    </ng-template>
                  </div>
                }
              </div>
            }
          </div>
        </ng-template>
      }

      @if (
        !hasStagingInfix() &&
        form
          .get(STEP_SETUP_FORM_GROUPS.STEP_SETUP)
          .get(STEP_SETUP_FORM_GROUPS.OPERATIONAL_DATA)
          .get(STEP_SETUP_FORM_GROUPS.CAPABILITIES)
          .get(CapabilityType.Metering)?.value &&
        form
          .get(STEP_SETUP_FORM_GROUPS.STEP_SETUP)
          .get(STEP_SETUP_FORM_GROUPS.METERING)
      ) {
        <div [formGroupName]="STEP_SETUP_FORM_GROUPS.METERING">
          <h3
            class="section-title"
            i18="XGRP|Step Setup Metering section title@@organizations.unified-step-setup.stepSetupMeteringSectionTitle"
          >
            Metering
          </h3>
          <app-container [body]="meteringAccount"></app-container>
          <ng-template #meteringAccount>
            <app-reactive-input
              i18-label="XFLD|Step Setup Local Tenant Type label@@organizations.unified-step-setup.stepSetuplocalTenantTypeLabel"
              label="Local Tenant Type"
              [formControlName]="STEP_SETUP_FORM_CONTROLS.LOCAL_TENANT_TYPE"
              popoverTitle="Local Tenant Type"
              [required]="true"
              [popoverDescription]="localTenantTypeInfoHelp"
              placeholder="Enter the local tenant type"
            >
            </app-reactive-input>
            <ng-template
              #localTenantTypeInfoHelp
              i18="XMSG|Step Setup Local Tenant Type popover body@@organizations.unified-step-setup.stepSetupLocalTenantTypePopoverBody"
            >
              Enter a unique identifier for your app. You can use any value you
              want, but it must be the same value as specified in the
              originServiceInstance.type field in your payload to Unified
              Metering. For example, you could use the CLD System Role or the
              Tenant Product Type of your app, but you may use any value that
              you prefer. <br /><br />
              The Local Tenant Type together with the ID of your local tenant
              are used to uniquely identify your app tenant/instance in CLD.
              <br /><br />
              This field will be added to both the generated Metering Account
              and Service Metadata resources.
            </ng-template>
          </ng-template>
        </div>
      }
    </div>
  </form>
</div>
